import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Grid3x3, UtensilsCrossed, Users, TrendingUp, Clock, BookOpen } from "lucide-react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [tables, setTables] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [rep, tabs] = await Promise.all([
          api.get("/admin/reports"),
          api.get("/tables"),
        ]);
        setData(rep.data);
        setTables(tabs.data);
      } catch (err) { console.error("admin dashboard load failed:", err); }
    })();
  }, []);

  const occupiedCount = tables.filter(t => t.status === "occupied").length;

  const stats = [
    { label: "Ventas hoy", value: formatCurrency(data?.today_sales || 0), icon: TrendingUp, accent: "#3e166c" },
    { label: "Semana", value: formatCurrency(data?.week_sales || 0), icon: Clock, accent: "#7248ae" },
    { label: "Mesas ocupadas", value: `${occupiedCount} / ${tables.length}`, icon: Grid3x3, accent: "#b182ec" },
    { label: "Pedidos pagados", value: data?.orders_count ?? 0, icon: UtensilsCrossed, accent: "#3e166c" },
  ];

  return (
    <div className="animate-fade-up" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Resumen</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Dashboard</h1>
        </div>
        <Link
          to="/manual"
          data-testid="dashboard-manual-link"
          className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-[#b182ec]/50 text-[#3e166c] hover:bg-[#3e166c]/5 hover:border-[#3e166c] transition-colors text-sm font-medium"
        >
          <BookOpen className="h-4 w-4" />
          Ver manual
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className={`p-5 border border-[#b182ec]/30 rounded-2xl card-glow animate-fade-up stagger-${i+1}`} data-testid={`stat-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-[#7248ae]">{s.label}</p>
                <Icon className="h-5 w-5" style={{ color: s.accent }} />
              </div>
              <p className="font-display text-2xl sm:text-3xl font-black text-[#1a0f2e]">{s.value}</p>
            </Card>
          );
        })}
      </div>

      <h2 className="font-display text-2xl font-bold text-[#1a0f2e] mb-4">Mesas en tiempo real</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {tables.map(t => (
          <Link to={`/admin/tables/${t.id}`} key={t.id}
            className={`p-4 rounded-2xl border transition-all hover:-translate-y-1 ${
              t.status === "occupied" ? "bg-[#3e166c] text-white border-[#3e166c]" : "bg-white text-[#1a0f2e] border-[#b182ec]/30"
            }`}
            data-testid={`dashboard-table-${t.number}`}
          >
            <p className="text-xs uppercase tracking-wider opacity-70">Mesa</p>
            <p className="font-display text-3xl font-black">{t.number}</p>
            <p className="text-xs mt-1 opacity-80">{t.status === "occupied" ? formatCurrency(t.current_total) : `${t.capacity} pers.`}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
